import { Link, useLocation } from "react-router-dom";

export interface SubNavItem {
  to: string;
  label: string;
  /** When true, only the exact path is active (no child-path matching). */
  exact?: boolean;
}

interface Props {
  ariaLabel: string;
  items: SubNavItem[];
}

/** Sticky secondary tab-bar. Designed to render inside a sticky wrapper so it
 *  pins below the main bar. Solid bg so scrolled content doesn't bleed through;
 *  horizontal overflow so tabs never wrap/clip on phones. */
export function SubNav({ ariaLabel, items }: Props): JSX.Element {
  const { pathname } = useLocation();
  return (
    <div className="border-b border-border bg-card shadow-sm">
      <nav className="max-w-6xl mx-auto flex gap-1 px-6 overflow-x-auto overflow-y-hidden" aria-label={ariaLabel}>
        {items.map((item) => (
          <SubNavTab key={item.to} item={item} pathname={pathname} />
        ))}
      </nav>
    </div>
  );
}

function SubNavTab({ item, pathname }: { item: SubNavItem; pathname: string }): JSX.Element {
  const active = item.exact
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`);
  return (
    <Link
      to={item.to}
      aria-current={active ? "page" : undefined}
      className={`whitespace-nowrap px-4 py-2 text-sm border-b-2 -mb-px ${
        active
          ? "border-primary text-foreground font-semibold"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {item.label}
    </Link>
  );
}
